import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc, collectionGroup, query, limit } from 'firebase/firestore';
import { auth } from '../firebase';

export const ACCESS_LEVELS = {
    ADMIN: 'admin',
    PARTNER: 'partner'
};

const MASTER_ADMIN = 'a.g.e.bioinf@gmail.com';

// Cache for user access data
let userAccessCache = new Map();
let lastCacheUpdate = null;
const CACHE_DURATION = 30 * 1000; // 30 seconds for testing

export const clearAccessCache = () => {
    console.log('Cache cleared');
    userAccessCache.clear();
    lastCacheUpdate = null;
};

// Function to check if a collection exists
const collectionExists = async (collectionName) => {
    try {
        const q = query(collectionGroup(db, collectionName), limit(1));
        const snapshot = await getDocs(q);
        return !snapshot.empty;
    } catch (error) {
        console.error(`Error checking if collection ${collectionName} exists:`, error);
        return false;
    }
};

// Function to initialize userAccess collection
export const initializeUserAccess = async () => {
    try {
        // Check if user is authenticated
        if (!auth.currentUser) {
            console.log('User not authenticated. Cannot initialize userAccess collection.');
            return false;
        }

        console.log('Checking userAccess collection with auth:', auth.currentUser.email);
        
        // Check if userAccess collection exists by trying to get any document
        const userAccessExists = await collectionExists('userAccess');
        
        if (!userAccessExists) {
            console.log('userAccess collection does not exist. Creating it with master admin...');
            // Create userAccess collection with master admin
            const masterAdminData = {
                level: ACCESS_LEVELS.ADMIN,
                scenarios: 'all',
                modes: ['text', 'mixed', 'full-voice'],
                lastUpdated: new Date()
            };
            
            const userRef = doc(db, 'userAccess', MASTER_ADMIN);
            await setDoc(userRef, masterAdminData);
            console.log('Master admin user created successfully.');
        } else {
            console.log('userAccess collection already exists.');
        }
        return true;
    } catch (error) {
        console.error('Error initializing userAccess collection:', error);
        return false;
    }
};

export const getUserAccess = async (email) => {
    // Check for master admin first
    if (email === MASTER_ADMIN) {
        return {
            level: ACCESS_LEVELS.ADMIN,
            scenarios: 'all'
        };
    }

    try {
        // Always fetch fresh data for testing
        const userDoc = await getDoc(doc(db, 'userAccess', email));
        if (userDoc.exists()) {
            const userData = userDoc.data();
            // Update cache
            userAccessCache.set(email, userData);
            lastCacheUpdate = Date.now();
            return userData;
        }
        return null;
    } catch (error) {
        console.error('Error fetching user access:', error);
        return null;
    }
};

export const isAdmin = async (email) => {
    if (email === MASTER_ADMIN) return true;
    const access = await getUserAccess(email);
    return access?.level === ACCESS_LEVELS.ADMIN;
};

export const isPartner = async (email) => {
    if (email === MASTER_ADMIN) return false;
    const access = await getUserAccess(email);
    return access?.level === ACCESS_LEVELS.PARTNER;
};

export const recordVisitor = async (email) => {
    try {
        const visitorRef = doc(db, 'visitors', email);
        await setDoc(visitorRef, {
            email,
            firstVisit: new Date(),
            lastVisit: new Date()
        }, { merge: true }); // This will update lastVisit but keep original firstVisit
    } catch (error) {
        console.error('Error recording visitor:', error);
    }
};

export const hasAccess = async (email) => {
    if (email === MASTER_ADMIN) return true;
    const access = await getUserAccess(email);
    
    if (!access) {
        // Record as visitor if no explicit access
        await recordVisitor(email);
        return false;
    }
    
    return true;
};

export const getAllowedScenarios = async (email) => {
    if (email === MASTER_ADMIN) return 'all';
    const access = await getUserAccess(email);
    if (!access) return [];
    return access.scenarios === 'all' ? 'all' : access.scenarios;
};

export const isMasterAdmin = (email) => {
    return email === MASTER_ADMIN;
}; 